import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { QuerySpec } from '@/types/query';
import { useQueryResult } from '@/hooks/useQueryResult';
import {
  buildCompareDeltas,
  pivotCompareRows,
  type CompareDeltaRow,
} from '@/engine/pivots/compare';
import {
  useCompareTableRegistration,
  type CompareTableSnapshot,
} from '@/features/export/compare-table-registry';
import { useWidgetRuntimeStore } from '@/store/widget-runtime-store';
import { formatAxisLabel } from './chart-axis-config';
import { WidgetContainer } from './WidgetContainer';
import { ReloadButton } from './ReloadButton';

type CompareTableWidgetProps = {
  /** Widget id, used by the compare-table registry for whole-dashboard PDF export. */
  widgetId: string;
  title: string;
  spec: QuerySpec;
  /** Top-N count per side. Defaults to 10. */
  limit?: number;
  onRemove?: (() => void) | undefined;
  onSettings?: (() => void) | undefined;
  note?: string | undefined;
};

/**
 * Karşılaştırma Tablosu — top N risers + top N fallers between the
 * two configured comparison periods (D-020).
 *
 * Each side renders five columns: category, period A value, period B
 * value, absolute difference, and percent change. Period column
 * headers come from `compareMeta.labelA` / `labelB` so the table
 * mirrors the active period selection.
 *
 * Only meaningful in compare mode. Because widgets are scoped to the
 * mode they were created in (D-020), instances of this kind only
 * exist in compare mode dashboards; an explicit empty state is shown
 * when the active query somehow returns non-compare data (e.g. mode
 * config was cleared after the widget was added).
 */
export function CompareTableWidget({
  widgetId,
  title,
  spec,
  limit = 10,
  onRemove,
  onSettings,
  note,
}: CompareTableWidgetProps): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, error, reload } = useQueryResult(spec);
  const groupBy = spec.groupBy[0] ?? '';
  const metricAlias = spec.metrics[0]?.alias ?? 'metric';

  const deltas = useMemo<CompareDeltaRow[] | null>(() => {
    if (!data?.compareMeta) return null;
    const pivot = pivotCompareRows(data.rows, groupBy, metricAlias);
    return buildCompareDeltas(pivot);
  }, [data, groupBy, metricAlias]);

  const winners = deltas?.filter((d) => d.delta > 0).slice(0, limit) ?? [];
  const losers =
    deltas
      ?.filter((d) => d.delta < 0)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, limit) ?? [];

  const labelA = data?.compareMeta?.labelA ?? '';
  const labelB = data?.compareMeta?.labelB ?? '';

  // Park the latest snapshot in a ref so the registry's getSnapshot
  // closure stays referentially stable — re-registering on every delta
  // update would cause the PDF button's listCompareTables() snapshot to
  // flicker mid-export.
  const snapshotRef = useRef<CompareTableSnapshot | null>(null);
  snapshotRef.current = data?.compareMeta
    ? { winners, losers, labelA, labelB, limit }
    : null;
  const getSnapshot = useCallback(() => snapshotRef.current, []);
  useCompareTableRegistration(widgetId, title, getSnapshot);

  // Publish the actual rendered row count to the runtime store so the
  // dashboard grid can size the cell to fit the data instead of the
  // user's Top-N cap (item: tablonun veri sayısına göre minimum
  // yükseklikte olması). Cleared on unmount so a stale row count from
  // a previous mode doesn't keep an empty grid cell tall.
  const setCompareTableRows = useWidgetRuntimeStore(
    (s) => s.setCompareTableRows,
  );
  const clearCompareTableRows = useWidgetRuntimeStore(
    (s) => s.clearCompareTableRows,
  );
  const renderedRows = Math.max(winners.length, losers.length);
  useEffect(() => {
    setCompareTableRows(widgetId, renderedRows);
  }, [widgetId, renderedRows, setCompareTableRows]);
  useEffect(() => {
    return () => clearCompareTableRows(widgetId);
  }, [widgetId, clearCompareTableRows]);

  return (
    <WidgetContainer
      title={title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      onSettings={onSettings}
      note={note}
      titleBarExtra={<ReloadButton onReload={reload} disabled={isLoading} />}
    >
      {!data?.compareMeta ? (
        <div
          className="flex items-center justify-center h-full p-4 text-xs text-center"
          style={{ color: 'var(--color-muted)' }}
          role="status"
        >
          {t('compare.table.onlyCompareMode')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 h-full overflow-auto p-2">
          <DeltaTable
            heading={t('compare.table.winnersHeader', { n: limit })}
            rows={winners}
            tone="success"
            labelA={labelA}
            labelB={labelB}
          />
          <DeltaTable
            heading={t('compare.table.losersHeader', { n: limit })}
            rows={losers}
            tone="danger"
            labelA={labelA}
            labelB={labelB}
          />
        </div>
      )}
    </WidgetContainer>
  );
}

// ---------------------------------------------------------------------------
// DeltaTable
// ---------------------------------------------------------------------------

type DeltaTableProps = {
  heading: string;
  rows: readonly CompareDeltaRow[];
  tone: 'success' | 'danger';
  labelA: string;
  labelB: string;
};

function DeltaTable({
  heading,
  rows,
  tone,
  labelA,
  labelB,
}: DeltaTableProps): JSX.Element {
  const { t } = useTranslation();
  const headerBg =
    tone === 'success'
      ? 'var(--color-success, #16a34a)'
      : 'var(--color-danger)';
  const fmt = (n: number): string => n.toLocaleString();
  return (
    <div
      className="rounded overflow-hidden flex flex-col"
      style={{ border: '1px solid var(--color-border)' }}
    >
      <div
        className="px-2 py-1.5 text-xs font-semibold"
        style={{ background: headerBg, color: '#fff' }}
      >
        {heading}
      </div>
      <div className="flex-1 overflow-auto">
        <table
          className="w-full text-[11px] tabular-nums"
          style={{ color: 'var(--color-fg)' }}
        >
          <thead>
            <tr style={{ background: 'var(--color-surface)' }}>
              <th className="text-left px-2 py-1 font-semibold">
                {t('compare.table.categoryCol')}
              </th>
              <th className="text-right px-2 py-1 font-semibold" title={labelA}>
                {labelA}
              </th>
              <th className="text-right px-2 py-1 font-semibold" title={labelB}>
                {labelB}
              </th>
              <th className="text-right px-2 py-1 font-semibold">
                {t('compare.table.diffCol')}
              </th>
              <th className="text-right px-2 py-1 font-semibold">
                {t('compare.table.percentCol')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-2 py-2 text-center"
                  style={{ color: 'var(--color-muted)' }}
                >
                  —
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                // pivotCompareRows normalises Date keys to ISO YYYY-MM-DD;
                // formatAxisLabel turns those into DD.MM.YYYY for display
                // (and is a no-op for plain category strings).
                const displayCategory = formatAxisLabel(r.category);
                return (
                <tr
                  key={r.category}
                  style={{ borderTop: '1px solid var(--color-border)' }}
                >
                  <td className="px-2 py-1 truncate" title={displayCategory}>
                    {displayCategory}
                  </td>
                  <td className="px-2 py-1 text-right">{fmt(r.valueA)}</td>
                  <td className="px-2 py-1 text-right">{fmt(r.valueB)}</td>
                  <td className="px-2 py-1 text-right">
                    {r.delta >= 0 ? '+' : ''}
                    {fmt(r.delta)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    {r.percent == null
                      ? '—'
                      : `${r.percent >= 0 ? '+' : ''}${(r.percent * 100).toFixed(1)}%`}
                  </td>
                </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
